/**
 * Microsoft Clarity — session recordings and heatmaps.
 *
 * Deliberately shaped like lib/analytics.ts: the tag is injected at runtime rather than sitting
 * in index.html so it can be switched off entirely. With no VITE_CLARITY_PROJECT_ID the script
 * is never fetched, which keeps local dev and preview builds out of the production project.
 * Nothing here throws — instrumentation must never be able to break a page.
 *
 * Why this exists alongside GA: GA says *which* step a visitor abandoned, Clarity shows the
 * recording of them abandoning it. The first real signup to reach the last onboarding step and
 * quit left nothing behind but a column value, which is exactly the gap recordings close.
 */

import { hasAnalyticsConsent, subscribeToConsent } from "@/lib/consent";

const PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

/** Whether a project ID is configured for this build. */
export const clarityEnabled = Boolean(PROJECT_ID);

let initialised = false;

function consentV2(analyticsGranted: boolean): void {
  window.clarity?.("consentv2", {
    ad_Storage: "denied",
    analytics_Storage: analyticsGranted ? "granted" : "denied",
  });
}

/**
 * Load the Clarity tag.
 *
 * Microsoft's Consent API v2: the tag loads on every visit in no-consent mode
 * (analytics_Storage "denied"), where Clarity collects without setting its _clck/_clsk cookies.
 * Accept grants analytics storage so normal cookie-based sessions start; Reject keeps it denied —
 * Clarity stays in no-consent mode rather than being switched off, but is never granted.
 */
export function initClarity(): void {
  if (initialised || !PROJECT_ID) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  initialised = true;

  /* Clarity's own snippet shape: calls made before the script loads are queued on
     window.clarity.q and replayed by the tag once it arrives. */
  const queue: (...args: unknown[]) => void = function clarityShim(...args: unknown[]) {
    (clarityShim as typeof queue & { q: unknown[] }).q =
      (clarityShim as typeof queue & { q?: unknown[] }).q || [];
    (clarityShim as typeof queue & { q: unknown[] }).q.push(args);
  };
  window.clarity = window.clarity || (queue as NonNullable<Window["clarity"]>);

  // Queued ahead of the tag so it replays before Clarity can write any cookie.
  consentV2(hasAnalyticsConsent());
  subscribeToConsent((decision) => consentV2(decision === "accepted"));

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(PROJECT_ID)}`;
  document.head.appendChild(script);
}

/**
 * Tag the current session with a custom key/value, filterable in the Clarity dashboard.
 *
 * Use sparingly and never for anything identifying: Clarity's own terms forbid sending PII,
 * and a recording is already the most sensitive thing we collect. No-ops when disabled.
 */
export function clarityTag(key: string, value: string): void {
  if (!PROJECT_ID || !window.clarity) return;
  window.clarity("set", key, value);
}

/**
 * Flag a session as worth keeping. Clarity samples aggressively under load, and an upgrade or a
 * dropped onboarding is exactly the session we would not want discarded.
 */
export function clarityUpgrade(reason: string): void {
  if (!PROJECT_ID || !window.clarity) return;
  window.clarity("upgrade", reason);
}
